import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Clock,
  User,
  FileText,
  CheckCircle,
  XCircle,
  Edit,
  Send,
  AlertCircle,
  Download,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const PermitHistoryDetail = ({ permitId, onDownloadZip, downloadingZip, userRole }) => {
  const canExport = ['adc', 'dc'].includes(userRole);
  const [expandedItems, setExpandedItems] = useState({});

  const { data, isLoading, error } = useQuery(
    ['permitHistory', permitId],
    async () => {
      const response = await api.get(`/permits/${permitId}/history`);
      return response.data;
    },
    {
      enabled: !!permitId
    }
  );

  const exportPDF = async () => {
    try {
      toast.loading('Generating PDF...', { id: 'pdf-export' });

      const response = await api.get(
        `/permits/${permitId}/history/export-pdf`,
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `permit-history-${permitId}-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF exported successfully!', { id: 'pdf-export' });
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF', { id: 'pdf-export' });
    }
  };

  const toggleExpand = (historyId) => {
    setExpandedItems(prev => ({
      ...prev,
      [historyId]: !prev[historyId]
    }));
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'updated':
        return <Edit className="w-5 h-5 text-yellow-500" />;
      case 'submitted':
        return <Send className="w-5 h-5 text-purple-500" />;
      case 'reviewed':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'returned':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'created':
        return 'bg-blue-100 text-blue-800';
      case 'updated':
        return 'bg-yellow-100 text-yellow-800';
      case 'submitted':
        return 'bg-purple-100 text-purple-800';
      case 'reviewed':
        return 'bg-orange-100 text-orange-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'returned':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const extractFieldChanges = (comments) => {
    if (!comments) return null;

    const changesSection = comments.split('Field Changes:')[1];
    if (!changesSection) return null;

    return changesSection
      .trim()
      .split('\n')
      .filter(line => line.trim().length > 0)
      .map(line => line.trim());
  };

  // Render change text with clickable document links
  const renderChangeWithLinks = (changeText) => {
    // Pattern to match [DOC:filename:s3Key]
    const docPattern = /\[DOC:(.*?):(.*?)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = docPattern.exec(changeText)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: changeText.substring(lastIndex, match.index)
        });
      }

      // Add the document link
      parts.push({
        type: 'link',
        filename: match[1],
        s3Key: match[2]
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < changeText.length) {
      parts.push({
        type: 'text',
        content: changeText.substring(lastIndex)
      });
    }

    // If no links found, return plain text
    if (parts.length === 0) {
      return <span>{changeText}</span>;
    }

    // Render parts with clickable links
    return (
      <span>
        {parts.map((part, idx) => {
          if (part.type === 'link') {
            return (
              <button
                key={idx}
                onClick={() => handleDocumentClick(part.s3Key, part.filename)}
                className="text-blue-600 hover:text-blue-800 underline font-medium mx-1"
                title="Click to download document"
              >
                {part.filename}
              </button>
            );
          }
          return <span key={idx}>{part.content}</span>;
        })}
      </span>
    );
  };

  // Handle document link click
  const handleDocumentClick = async (s3Key, filename) => {
    try {
      toast.loading(`Preparing ${filename}...`, { id: 'doc-download' });

      // Determine document type and index from s3Key
      const isLandRegistry = s3Key.includes('landRegistryDocument');

      let params;
      if (isLandRegistry) {
        params = new URLSearchParams({ documentType: 'landRegistry' });
      } else {
        // For supporting documents, we need to find the index
        // This is a simplified approach - in production you might need to fetch the permit data
        toast.dismiss('doc-download');
        toast.info('Opening document...');
        window.open(`/permits/${permitId}`, '_blank');
        return;
      }

      const response = await api.get(`/permits/${permitId}/documents/download?${params}`);

      if (response.data.success) {
        window.open(response.data.downloadUrl, '_blank');
        toast.success(`Opening ${filename}`, { id: 'doc-download' });
      } else {
        toast.error('Failed to generate download link', { id: 'doc-download' });
      }
    } catch (error) {
      console.error('Document download error:', error);
      toast.error('Failed to download document', { id: 'doc-download' });
    }
  };

  // Render comments with clickable edit/cancellation request links
  const renderCommentsWithLinks = (comments) => {
    if (!comments) return null;

    // Pattern to match [EDIT_REQUEST:uuid] or [CANCEL_REQUEST:uuid]
    const linkPattern = /\[(EDIT_REQUEST|CANCEL_REQUEST):([a-f0-9-]{36})\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkPattern.exec(comments)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: comments.substring(lastIndex, match.index)
        });
      }

      // Add the request link
      parts.push({
        type: match[1] === 'CANCEL_REQUEST' ? 'cancelRequest' : 'editRequest',
        id: match[2]
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < comments.length) {
      parts.push({
        type: 'text',
        content: comments.substring(lastIndex)
      });
    }

    // If no links found, return plain text
    if (parts.length === 0) {
      return <span>{comments}</span>;
    }

    // Render parts with clickable links
    return (
      <span>
        {parts.map((part, idx) => {
          if (part.type === 'editRequest') {
            return (
              <Link
                key={idx}
                to={`/edit-requests/${part.id}`}
                className="text-blue-600 hover:text-blue-800 underline font-medium"
              >
                Edit request
              </Link>
            );
          }
          if (part.type === 'cancelRequest') {
            return (
              <Link
                key={idx}
                to={`/cancellation-requests/${part.id}`}
                className="text-blue-600 hover:text-blue-800 underline font-medium"
              >
                Cancellation request
              </Link>
            );
          }
          return <span key={idx}>{part.content}</span>;
        })}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error loading history: {error.message}</p>
      </div>
    );
  }

  const { history } = data || {};

  if (!history || history.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-600">No history records found</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      {/* Header with Export Buttons */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Audit Trail History
        </h3>
        <div className="flex items-center gap-3">
          {/* Download All Documents as ZIP */}
          {onDownloadZip && (
            <button
              onClick={onDownloadZip}
              disabled={downloadingZip}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium shadow-sm transition-colors ${
                downloadingZip
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              title="Download all documents as ZIP"
            >
              <Download className="w-4 h-4" />
              {downloadingZip ? 'Creating ZIP...' : 'Download Files'}
            </button>
          )}

          {/* Export History PDF - ADC and DC only */}
          {canExport && (
            <button
              onClick={exportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
              title="Export audit trail history as PDF"
            >
              <Download className="w-4 h-4" />
              Export History
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {/* History items */}
        <div className="space-y-6">
          {history.map((entry, index) => {
            const isExpanded = expandedItems[entry.id];
            const fieldChanges = extractFieldChanges(entry.comments);
            const hasFieldChanges = fieldChanges && fieldChanges.length > 0;

            return (
              <div key={entry.id} className="relative pl-14">
                {/* Icon */}
                <div className="absolute left-0 top-0 w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center">
                  {getActionIcon(entry.action)}
                </div>

                {/* Content */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getActionColor(entry.action)}`}>
                        {entry.action.toUpperCase()}
                      </span>
                      <p className="mt-2 text-sm text-gray-900 font-medium">
                        {entry.performer?.fullName || 'Unknown User'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {entry.performer?.role && `${entry.performer.role}${entry.performer.designation ? ` - ${entry.performer.designation}` : ''}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(entry.created_at)}
                      </p>
                      {entry.ipAddress && (
                        <p className="text-xs text-gray-500 mt-1">
                          IP: {entry.ipAddress}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Comments */}
                  {entry.comments && !hasFieldChanges && (
                    <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                      <p className="text-sm text-gray-700">{renderCommentsWithLinks(entry.comments)}</p>
                    </div>
                  )}

                  {/* Field Changes */}
                  {hasFieldChanges && (
                    <div className="mt-3">
                      <button
                        onClick={() => toggleExpand(entry.id)}
                        className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                        {isExpanded ? 'Hide' : 'Show'} Field Changes ({fieldChanges.length})
                      </button>

                      {isExpanded && (
                        <div className="mt-2 p-3 bg-white rounded border border-gray-200">
                          <div className="space-y-2">
                            {fieldChanges.map((change, idx) => (
                              <div key={idx} className="text-sm">
                                <p className="text-gray-700 font-mono text-xs bg-gray-50 p-2 rounded">
                                  {renderChangeWithLinks(change)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Total {history.length} record{history.length !== 1 ? 's' : ''} in audit trail
        </p>
      </div>
    </div>
  );
};

export default PermitHistoryDetail;
